import { useState, useEffect, useCallback, useRef } from 'react';
import { adminApi } from '../../lib/adminApi';
import { toast } from 'sonner';
import {
  MagnifyingGlass,
  Prohibit,
  CheckCircle,
  Trash,
  ArrowClockwise,
  Warning,
  Users as UsersIcon,
  ShieldCheck,
  CaretDown,
  User as UserIcon,
} from '@phosphor-icons/react';
import { Button } from '../../components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { useAuth } from '../../contexts/AuthContext';

const PAGE_LIMIT = 20;

export default function AdminUsers() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [roleTarget, setRoleTarget] = useState(null); // { user, newRole }
  const debounceRef = useRef(null);

  const fetchUsers = useCallback(async (currentPage, currentSearch) => {
    try {
      setLoading(true);
      setError(false);
      const params = { page: currentPage, limit: PAGE_LIMIT };
      if (currentSearch) params.search = currentSearch;
      const res = await adminApi.getUsers(params);
      setUsers(res.items || []);
      setTotal(res.total || 0);
    } catch (err) {
      console.error('Failed to load users', err);
      setError(true);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers(page, search);
  }, [fetchUsers, page, search]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearch(value);
      setPage(1);
    }, 300);
  };

  const handleToggleDisable = async (user) => {
    const action = user.disabled ? 'enable' : 'disable';
    try {
      setActionLoading(user.user_id);
      if (user.disabled) {
        await adminApi.enableUser(user.user_id);
      } else {
        await adminApi.disableUser(user.user_id);
      }
      toast.success(`User ${action}d successfully`);
      fetchUsers(page, search);
    } catch (err) {
      toast.error(err.message || `Failed to ${action} user`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleRoleChange = async () => {
    if (!roleTarget) return;
    const { user, newRole } = roleTarget;
    try {
      setActionLoading(user.user_id);
      await adminApi.setUserRole(user.user_id, newRole);
      toast.success(
        newRole === 'super_admin'
          ? `${user.name || user.email} promoted to super admin`
          : `${user.name || user.email} demoted to user`,
      );
      setRoleTarget(null);
      fetchUsers(page, search);
    } catch (err) {
      toast.error(err.message || 'Failed to update role');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      setActionLoading(deleteTarget.user_id);
      await adminApi.deleteUser(deleteTarget.user_id);
      toast.success('User deleted successfully');
      setDeleteTarget(null);
      fetchUsers(page, search);
    } catch (err) {
      toast.error(err.message || 'Failed to delete user');
    } finally {
      setActionLoading(null);
    }
  };

  const initials = (name, email) =>
    (name || email || '?').slice(0, 2).toUpperCase();

  const totalPages = Math.ceil(total / PAGE_LIMIT);

  if (loading && users.length === 0) {
    return (
      <div className="p-6 md:p-10 animate-fade-in max-w-[1280px] mx-auto w-full">
        <div className="h-9 w-48 bg-muted rounded-xl animate-pulse mb-6" />
        <div className="h-12 w-80 bg-muted rounded-xl animate-pulse mb-6" />
        <div className="surface-card overflow-hidden">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-16 border-t border-border/70 animate-pulse first:border-t-0" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 md:p-10 animate-fade-in" data-testid="admin-users-error">
        <div className="surface-card p-12 flex flex-col items-center justify-center text-center max-w-2xl mx-auto">
          <div className="grid place-items-center h-14 w-14 rounded-2xl bg-destructive/10 text-destructive mb-4">
            <Warning size={26} weight="fill" />
          </div>
          <h2 className="font-display text-2xl font-extrabold text-foreground mb-2">
            Failed to load users
          </h2>
          <p className="text-sm text-muted-foreground mb-6 max-w-md">
            We couldn't fetch the user list. Check your connection and try again.
          </p>
          <Button
            onClick={() => fetchUsers(page, search)}
            data-testid="retry-btn"
            className="rounded-xl gradient-primary text-white hover:opacity-95 px-6 py-2.5 font-semibold gap-2"
          >
            <ArrowClockwise size={16} weight="bold" />
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-10 animate-fade-in max-w-[1280px] mx-auto w-full" data-testid="admin-users-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-3xl sm:text-[2.5rem] font-extrabold tracking-tight text-foreground leading-tight">
            Users
          </h1>
          <p className="text-sm text-muted-foreground mt-2">
            <span className="font-medium text-foreground/90">{total}</span> total user
            {total !== 1 ? 's' : ''} across the platform.
          </p>
        </div>
        <div className="relative w-full sm:w-80">
          <MagnifyingGlass
            size={16}
            className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
          />
          <input
            type="search"
            placeholder="Search by name or email…"
            onChange={handleSearchChange}
            className="input-modern pl-10"
            data-testid="search-input"
          />
        </div>
      </div>

      <div className="surface-card overflow-hidden ambient-shadow">
        <div className="overflow-x-auto">
          <table className="w-full text-sm table-modern">
            <thead>
              <tr>
                <th className="text-left">User</th>
                <th className="text-left">Created</th>
                <th className="text-right">Workspaces</th>
                <th className="text-left">Role</th>
                <th className="text-left">Status</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-16">
                    <div className="flex flex-col items-center text-muted-foreground">
                      <UsersIcon size={32} weight="duotone" className="mb-2 text-muted-foreground/70" />
                      <span className="text-sm">
                        {search ? 'No users match your search.' : 'No users found.'}
                      </span>
                    </div>
                  </td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr key={user.user_id} className="link-row">
                    <td>
                      <div className="flex items-center gap-3">
                        <span className="grid place-items-center h-9 w-9 rounded-xl bg-primary/10 text-primary text-xs font-semibold shrink-0">
                          {initials(user.name, user.email)}
                        </span>
                        <div className="min-w-0">
                          <p className="font-semibold text-foreground truncate">
                            {user.name || '—'}
                          </p>
                          <p className="text-xs text-muted-foreground truncate">
                            {user.email}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="text-muted-foreground">
                      {user.created_at
                        ? new Date(user.created_at).toLocaleDateString()
                        : '—'}
                    </td>
                    <td className="text-right font-semibold text-foreground tabular-nums">
                      {user.workspace_count ?? 0}
                    </td>
                    <td>
                      {(() => {
                        const isSelf = me?.user_id === user.user_id;
                        const role = user.role || 'user';
                        const isAdmin = role === 'super_admin';
                        return (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button
                                type="button"
                                disabled={actionLoading === user.user_id}
                                className={`chip transition hover:opacity-90 ${
                                  isAdmin
                                    ? 'bg-primary/10 text-primary border border-primary/15'
                                    : 'bg-muted text-muted-foreground border border-border/70'
                                }`}
                                data-testid={`role-trigger-${user.user_id}`}
                              >
                                {isAdmin ? <ShieldCheck size={11} weight="fill" /> : <UserIcon size={11} weight="fill" />}
                                {role}
                                <CaretDown size={10} weight="bold" className="opacity-70" />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="start" className="w-[220px]">
                              <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                                Platform role
                              </DropdownMenuLabel>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                disabled={role === 'user' || isSelf}
                                onSelect={() => setRoleTarget({ user, newRole: 'user' })}
                                data-testid={`set-role-user-${user.user_id}`}
                              >
                                <UserIcon size={14} className="mr-2" />
                                <div className="flex-1">
                                  <p className="text-sm font-medium">User</p>
                                  <p className="text-[11px] text-muted-foreground">No admin access</p>
                                </div>
                                {role === 'user' && <CheckCircle size={14} className="text-emerald-600" />}
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                disabled={role === 'super_admin'}
                                onSelect={() => setRoleTarget({ user, newRole: 'super_admin' })}
                                data-testid={`set-role-admin-${user.user_id}`}
                              >
                                <ShieldCheck size={14} className="mr-2 text-primary" />
                                <div className="flex-1">
                                  <p className="text-sm font-medium">Super admin</p>
                                  <p className="text-[11px] text-muted-foreground">Full /admin access</p>
                                </div>
                                {role === 'super_admin' && <CheckCircle size={14} className="text-emerald-600" />}
                              </DropdownMenuItem>
                              {isSelf && (
                                <>
                                  <DropdownMenuSeparator />
                                  <div className="px-2 py-1.5 text-[11px] text-muted-foreground">
                                    You can't demote yourself.
                                  </div>
                                </>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        );
                      })()}
                    </td>
                    <td>
                      <span
                        className={`chip ${
                          user.disabled
                            ? 'bg-rose-50 text-rose-600'
                            : 'bg-emerald-50 text-emerald-700'
                        }`}
                      >
                        <span
                          className={`inline-block h-1.5 w-1.5 rounded-full ${
                            user.disabled ? 'bg-rose-500' : 'bg-emerald-500'
                          }`}
                        />
                        {user.disabled ? 'Disabled' : 'Active'}
                      </span>
                    </td>
                    <td className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleToggleDisable(user)}
                          disabled={actionLoading === user.user_id}
                          className="h-8 w-8 p-0 rounded-lg hover:bg-muted"
                          title={user.disabled ? 'Enable user' : 'Disable user'}
                          data-testid={`toggle-disable-${user.user_id}`}
                        >
                          {user.disabled ? (
                            <CheckCircle size={16} className="text-emerald-600" />
                          ) : (
                            <Prohibit size={16} className="text-amber-600" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeleteTarget(user)}
                          disabled={actionLoading === user.user_id}
                          className="h-8 w-8 p-0 rounded-lg hover:bg-destructive/10"
                          title="Delete user"
                          data-testid={`delete-${user.user_id}`}
                        >
                          <Trash size={16} className="text-destructive" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-border/70 bg-muted/20">
            <p className="text-xs text-muted-foreground">
              Page <span className="font-semibold text-foreground">{page}</span> of {totalPages}
              <span className="text-muted-foreground/70"> · {total} users</span>
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1 || loading}
                className="rounded-lg text-xs h-8 px-3"
                data-testid="prev-page"
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages || loading}
                className="rounded-lg text-xs h-8 px-3"
                data-testid="next-page"
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>

      <AlertDialog
        open={!!roleTarget}
        onOpenChange={(open) => !open && setRoleTarget(null)}
      >
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {roleTarget?.newRole === 'super_admin'
                ? 'Promote to super admin?'
                : 'Demote to user?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {roleTarget?.newRole === 'super_admin' ? (
                <>
                  <span className="font-semibold text-foreground">
                    {roleTarget?.user?.name || roleTarget?.user?.email}
                  </span>{' '}
                  will gain full access to the admin console, including the
                  ability to manage every user, workspace, link, and platform
                  setting.
                </>
              ) : (
                <>
                  <span className="font-semibold text-foreground">
                    {roleTarget?.user?.name || roleTarget?.user?.email}
                  </span>{' '}
                  will lose access to the admin console. They'll still keep
                  their workspace memberships.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRoleChange}
              className={`rounded-xl ${
                roleTarget?.newRole === 'super_admin'
                  ? 'gradient-primary text-white'
                  : 'bg-amber-500 text-white hover:bg-amber-600'
              }`}
              data-testid="confirm-role-change"
            >
              {roleTarget?.newRole === 'super_admin' ? 'Promote' : 'Demote'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
      >
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete user</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete{' '}
              <span className="font-semibold text-foreground">
                {deleteTarget?.name || deleteTarget?.email}
              </span>
              ? This will remove the user and all their workspace memberships. This
              action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90 rounded-xl"
              data-testid="confirm-delete"
            >
              Delete user
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
